// framework/client/core/PWAPlatform.js
import { PlatformManager } from "./PlatformManager.js";

export class PWAPlatform extends PlatformManager {
    constructor(config) {
        super(config);
        this.platformName = 'pwa';
        this.isOnline = navigator.onLine;
        this.isForeground = document.visibilityState === 'visible';
        this.swRegistration = null;
        this.swEnabled = false;
    }

    async initialize() {
        await super.initialize();
        window.addEventListener('online', () => this._updateNetworkStatus(true));
        window.addEventListener('offline', () => this._updateNetworkStatus(false));
        document.addEventListener('visibilitychange', () => this._updateAppState());

        // Check if service worker is supported and has any registration
        if ('serviceWorker' in navigator) {
            try {
                // Check if there's already a registration
                const existingRegistration = await navigator.serviceWorker.getRegistration();

                if (existingRegistration) {
                    // Wait for it to be ready with a timeout
                    this.swRegistration = await Promise.race([
                        navigator.serviceWorker.ready,
                        new Promise((_, reject) =>
                            setTimeout(() => reject(new Error('Service worker ready timeout')), 5000)
                        )
                    ]);
                    this.swEnabled = true;
                    this.log.info('Service worker is ready and enabled.');
                } else {
                    this.log.info('No service worker registration found. PWA features will be limited.');
                    this.swEnabled = false;
                }
            } catch (error) {
                this.log.warn('Service worker initialization failed or timed out:', error.message);
                this.swEnabled = false;
            }
        } else {
            this.log.info('Service workers are not supported in this browser.');
            this.swEnabled = false;
        }
    }

    _updateNetworkStatus(isOnline) {
        this.isOnline = isOnline;
        this.log.info(`Network status changed: ${isOnline ? 'Online' : 'Offline'}`);
        this.emit('platform:networkStatusChanged', { connected: this.isOnline, type: 'wifi' });
    }

    _updateAppState() {
        this.isForeground = document.visibilityState === 'visible';
        this.log.info(`App state changed: ${this.isForeground ? 'Foreground' : 'Background'}`);
        this.emit('platform:appStateChanged', { isActive: this.isForeground });
    }

    async checkPermission(permissionType) {
        if (permissionType === 'notifications' && typeof Notification !== 'undefined') {
            const status = Notification.permission;
            return { status: status === 'default' ? 'prompt' : status };
        }

        let permName;
        switch (permissionType) {
            case 'notifications': permName = 'notifications'; break;
            case 'microphone': permName = 'microphone'; break;
            case 'camera': permName = 'camera'; break;
            case 'geolocation': permName = 'geolocation'; break;
            case 'backgroundSync': permName = 'periodic-background-sync'; break;
            case 'screenCapture': return { status: 'prompt' };
            default: throw new Error(`Unsupported permission type for PWA: ${permissionType}`);
        }

        try {
            const result = await navigator.permissions.query({ name: permName });
            return { status: result.state };
        } catch (error) {
            this.log.error(`Error checking permission '${permissionType}':`, error);
            return { status: 'denied' };
        }
    }

    async requestPermission(permissionType) {
        try {
            switch (permissionType) {
                case 'notifications':
                    const result = await Notification.requestPermission();
                    return { status: result };
                case 'microphone':
                case 'camera':
                    const constraint = permissionType === 'microphone' ? { audio: true } : { video: true };
                    await navigator.mediaDevices.getUserMedia(constraint);
                    return { status: 'granted' };
                case 'geolocation':
                    return new Promise((resolve) => {
                        navigator.geolocation.getCurrentPosition(
                            () => resolve({ status: 'granted' }),
                            () => resolve({ status: 'denied' })
                        );
                    });
                case 'screenCapture':
                    await navigator.mediaDevices.getDisplayMedia({ video: true });
                    return { status: 'granted' };
                default:
                    throw new Error(`Unsupported permission request for PWA: ${permissionType}`);
            }
        } catch (error) {
            this.log.warn(`Permission request for '${permissionType}' was denied or failed:`, error.name);
            return { status: 'denied' };
        }
    }

    async setAppBadge(count) {
        if ('setAppBadge' in navigator) {
            try {
                await navigator.setAppBadge(count);
            } catch (error) {
                this.log.warn('Failed to set app badge:', error.message);
            }
        } else {
            this.log.warn('App badging is not supported by this browser.');
        }
    }

    async clearAppBadge() {
        if ('clearAppBadge' in navigator) {
            try {
                await navigator.clearAppBadge();
            } catch (error) {
                this.log.warn('Failed to clear app badge:', error.message);
            }
        }
    }

    async applyUpdate() {
        if (!this.swEnabled) {
            this.log.warn('applyUpdate called, but service worker is not enabled.');
            return;
        }

        if (this.swRegistration && this.swRegistration.waiting) {
            this.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
            this.log.info('Sent SKIP_WAITING message to service worker.');
        } else {
            this.log.warn('applyUpdate called, but no waiting service worker found.');
        }
    }

    async openExternalUrl(url, options = {}) {
        try {
            // Optional Confirmation Overlay
            if (options.showConfirmOverlay) {
                const confirmed = await this.appClient.notifier.confirm(
                    this.t("You are leaving the application to visit an external link. Do you want to proceed?"),
                    {
                        title: this.t("Leaving Application"),
                        okButtonText: this.t("Proceed"),
                        cancelButtonText: this.t("Cancel")
                    }
                );
                if (!confirmed) {
                    return { success: false, error: 'Cancelled by user' };
                }
            }

            // Electron Support
            if (window.electronAPI && typeof window.electronAPI.openExternalUrl === 'function') {
                await window.electronAPI.openExternalUrl(url);
                return { success: true };
            }

            // Capacitor Support (Direct)
            const capacitorBrowser = window.Capacitor?.Plugins?.Browser || window.CapacitorBrowser;
            if (window.Capacitor?.isNativePlatform?.() && capacitorBrowser?.open) {
                await capacitorBrowser.open({ url });
                return { success: true };
            }

            // Capacitor Support (via Iframe Parent Bridge)
            if (window.parent && window.parent !== window) {
                return new Promise((resolve) => {
                    const requestId = `open-url-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                    const timeout = setTimeout(() => {
                        window.removeEventListener('message', onMessage);
                        resolve({ success: false, error: 'Timed out waiting for native browser bridge.' });
                    }, 10000);

                    const onMessage = (event) => {
                        if (event.source !== window.parent || event.data?.type !== 'appbuilder:openExternalUrlResult' || event.data?.requestId !== requestId) {
                            return;
                        }

                        clearTimeout(timeout);
                        window.removeEventListener('message', onMessage);
                        resolve(event.data.result || { success: true });
                    };

                    window.addEventListener('message', onMessage);
                    window.parent.postMessage({ type: 'appbuilder:openExternalUrl', requestId, url }, '*');
                });
            }

            // Standard Web Fallback
            window.open(url, '_blank', 'noopener,noreferrer');
            return { success: true };
        } catch (error) {
            this.log.error('Failed to open external URL:', error);
            return { success: false, error: error.message };
        }
    }
}